/**
 * Gmail Client — browser-based Gmail API for search + read.
 * Port of mittens email pipeline search.ts + read.ts.
 * Uses browser fetch, no backend needed.
 */

const GMAIL_API = 'https://gmail.googleapis.com/gmail/v1/users/me';

/**
 * Build a Gmail search query string from parameters.
 */
export function buildQuery({ keywords = [], senders = [], timeRange = {} } = {}) {
  const parts = [];

  if (senders.length) {
    const from = senders.map(s => `from:${s}`).join(' OR ');
    parts.push(`(${from})`);
  }

  if (keywords.length) {
    parts.push(keywords.join(' '));
  }

  if (timeRange.after) {
    parts.push(`after:${timeRange.after.replace(/-/g, '/')}`);
  }

  if (timeRange.before) {
    parts.push(`before:${timeRange.before.replace(/-/g, '/')}`);
  }

  return parts.join(' ');
}

/**
 * Search Gmail for matching messages.
 * @param {string} token - Gmail access token
 * @param {string} query - Gmail search query
 * @param {number} limit - Max number of emails to fetch
 * @returns {Promise<Array>} Array of email candidates with metadata
 */
export async function searchEmails(token, query, limit = 100) {
  if (!query.trim()) throw new Error('Empty search query');

  let messageIds = [];
  let pageToken = null;

  while (messageIds.length < limit) {
    const fetchCount = Math.min(500, limit - messageIds.length);
    let searchUrl = `${GMAIL_API}/messages?q=${encodeURIComponent(query)}&maxResults=${fetchCount}`;
    if (pageToken) searchUrl += `&pageToken=${pageToken}`;

    const searchRes = await fetch(searchUrl, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!searchRes.ok) {
      if (searchRes.status === 401) throw new Error('Gmail token expired — please reconnect');
      const errText = await searchRes.text();
      throw new Error(`Gmail search failed: ${errText.slice(0, 200)}`);
    }

    const searchData = await searchRes.json();
    if (searchData.messages) {
      messageIds = messageIds.concat(searchData.messages);
    }

    pageToken = searchData.nextPageToken;
    if (!pageToken) break; // No more results
  }

  if (messageIds.length === 0) return [];

  // Fetch metadata for each message
  const candidates = [];

  for (const { id, threadId } of messageIds) {
    try {
      const msgUrl = `${GMAIL_API}/messages/${id}?format=metadata&metadataHeaders=From&metadataHeaders=To&metadataHeaders=Subject&metadataHeaders=Date`;
      const msgRes = await fetch(msgUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!msgRes.ok) continue;
      const msg = await msgRes.json();

      const headers = msg.payload?.headers || [];
      const getHeader = (name) =>
        headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || '';

      candidates.push({
        id,
        threadId,
        from: getHeader('From'),
        to: getHeader('To'),
        subject: getHeader('Subject'),
        date: getHeader('Date'),
        snippet: msg.snippet || ''
      });
    } catch {
      // Skip failed individual fetches
    }
  }

  return candidates;
}

/**
 * Fetch and clean the full body of an email by ID.
 * @param {string} token - Gmail access token
 * @param {object} candidate - Email candidate object
 * @returns {Promise<object>} Candidate with cleanedBody and imageUrls populated
 */
export async function readEmail(token, candidate) {
  const url = `${GMAIL_API}/messages/${candidate.id}?format=full`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!res.ok) throw new Error(`Failed to fetch email ${candidate.id}`);
  const msg = await res.json();

  const { body: rawBody, mimePartsMap } = extractBody(msg.payload);
  const extractedImages = extractImagesFromHtml(rawBody, mimePartsMap);
  const cleaned = sanitizeHtml(rawBody);

  return { ...candidate, cleanedBody: cleaned, imageUrls: extractedImages };
}

/**
 * Read multiple emails. Stops after maxReads to avoid rate limits.
 */
export async function readEmails(token, candidates, maxReads = 25) {
  const results = [];
  const toRead = candidates.slice(0, maxReads);

  for (const candidate of toRead) {
    try {
      const read = await readEmail(token, candidate);
      results.push(read);
    } catch {
      results.push(candidate);
    }
  }

  return results;
}

// ── MIME Parts Collection ──

/**
 * Recursively walk MIME parts and collect Content-ID → data URI mappings.
 * @param {object} part - A MIME part from the Gmail API payload
 * @param {Map} map - Map to populate with contentId → data URI entries
 */
function collectMimeParts(part, map) {
  const contentId = (part.headers || []).find(h => h.name.toLowerCase() === 'content-id')?.value;
  if (contentId && part.body?.data) {
    const cleanId = contentId.replace(/[<>]/g, '');
    const mime = part.mimeType || 'image/jpeg';
    map.set(cleanId, `data:${mime};base64,${part.body.data.replace(/-/g, '+').replace(/_/g, '/')}`);
  }
  if (part.parts) {
    for (const child of part.parts) {
      collectMimeParts(child, map);
    }
  }
}

// ── Body Extraction ──

/**
 * Extract the email body text and MIME parts map from a Gmail payload.
 * @param {object} payload - Gmail API message payload
 * @returns {{ body: string, mimePartsMap: Map<string, string> }}
 */
function extractBody(payload) {
  if (!payload) return { body: '', mimePartsMap: new Map() };

  const mimePartsMap = new Map();
  collectMimeParts(payload, mimePartsMap);

  // Find the body text (prefer HTML, fall back to plain text)
  const bodyText = findBodyText(payload);

  return { body: bodyText, mimePartsMap };
}

/**
 * Recursively find the text body from a MIME payload.
 * @param {object} payload
 * @returns {string}
 */
function findBodyText(payload) {
  if (!payload) return '';

  if (payload.body?.data) {
    return base64Decode(payload.body.data);
  }

  if (payload.parts) {
    // Prefer text/html over text/plain
    const htmlPart = payload.parts.find(p => p.mimeType === 'text/html');
    if (htmlPart?.body?.data) return base64Decode(htmlPart.body.data);

    const textPart = payload.parts.find(p => p.mimeType === 'text/plain');
    if (textPart?.body?.data) return base64Decode(textPart.body.data);

    for (const part of payload.parts) {
      if (part.parts) {
        const nested = findBodyText(part);
        if (nested) return nested;
      }
    }
  }

  return '';
}

function base64Decode(data) {
  try {
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
    return atob(base64);
  } catch {
    return data;
  }
}

// ── Image Extraction ──

/**
 * Extract product image URLs from raw HTML, filtering out tracking pixels,
 * logos, spacers, and other non-product images.
 * @param {string} rawHtml - Raw HTML email body
 * @param {Map<string, string>} mimePartsMap - Map of Content-ID → base64 data URIs
 * @returns {string[]} Array of image URL strings
 */
function extractImagesFromHtml(rawHtml, mimePartsMap) {
  if (!rawHtml) return [];

  const images = [];
  const imgRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
  let match;

  while ((match = imgRegex.exec(rawHtml)) !== null) {
    const fullTag = match[0];
    const src = match[1];

    // Extract width and height attributes
    const widthMatch = fullTag.match(/width\s*=\s*["']?(\d+)/i);
    const heightMatch = fullTag.match(/height\s*=\s*["']?(\d+)/i);
    const width = widthMatch ? parseInt(widthMatch[1], 10) : null;
    const height = heightMatch ? parseInt(heightMatch[1], 10) : null;

    // Filter out tracking pixels (1x1 or similar)
    if (width === 1 || height === 1) continue;

    // Filter out tracking/beacon URLs
    if (/track|beacon|pixel|open|click|unsubscribe/i.test(src)) continue;

    // Filter out logos and social icons
    if (/logo|icon|header|footer|social|facebook|twitter|instagram/i.test(src)) continue;

    // Filter out spacer/blank images
    if (/spacer|blank|transparent/i.test(src)) continue;

    // Handle cid: references (inline MIME attachments)
    if (src.startsWith('cid:')) {
      const cid = src.slice(4);
      const dataUri = mimePartsMap.get(cid);
      if (dataUri) {
        images.push(dataUri);
      }
      continue;
    }

    // Keep base64 data URIs as-is
    if (src.startsWith('data:image/')) {
      images.push(src);
      continue;
    }

    // Keep HTTP/HTTPS URLs that passed all filters
    if (src.startsWith('http://') || src.startsWith('https://')) {
      images.push(src);
    }
  }

  return images;
}

// ── HTML Sanitization ──

function sanitizeHtml(html) {
  if (!html) return '';

  let text = html;

  // Remove style and script blocks
  text = text.replace(/<style[\s\S]*?<\/style>/gi, '');
  text = text.replace(/<script[\s\S]*?<\/script>/gi, '');

  // Remove tracking pixels
  text = text.replace(/<img[^>]*(?:width|height)\s*=\s*["']?1["']?[^>]*>/gi, '');
  text = text.replace(/<img[^>]*(?:tracking|beacon|pixel|open)[^>]*>/gi, '');

  // Remove comments and footers
  text = text.replace(/<!--[\s\S]*?-->/g, '');
  text = text.replace(/<footer[\s\S]*?<\/footer>/gi, '');

  // HTML entities
  text = text.replace(/&nbsp;/gi, ' ');
  text = text.replace(/&amp;/gi, '&');
  text = text.replace(/&lt;/gi, '<');
  text = text.replace(/&gt;/gi, '>');
  text = text.replace(/&quot;/gi, '"');
  text = text.replace(/&#39;/gi, "'");
  text = text.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code)));

  // Block elements to newlines
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/(?:p|div|tr|li|h[1-6]|td)>/gi, '\n');
  text = text.replace(/<(?:p|div|tr|li|h[1-6])[\s>]/gi, '\n');

  // Strip all style attributes (wastes LLM tokens)
  text = text.replace(/\s*style="[^"]*"/gi, '');
  text = text.replace(/\s*style='[^']*'/gi, '');
  text = text.replace(/\s*class="[^"]*"/gi, '');

  // Strip all remaining tags
  text = text.replace(/<[^>]+>/g, '');

  // Clean up whitespace
  text = text.replace(/[ \t]+/g, ' ');
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.replace(/^\s+|\s+$/gm, '');

  // Truncate for AI context window
  if (text.length > 2500) {
    text = text.slice(0, 2500) + '...';
  }

  return text.trim();
}
